import React from 'react';
import { Activity, UserCircle } from 'lucide-react';

const AdditionalParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  activityLevels,
  bodyTypes
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <UserCircle className="w-5 h-5 mr-2 text-emerald-500" />
        Дополнительные параметры
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Уровень физической активности */}
        <div>
          <label htmlFor="activityLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Activity className="w-4 h-4 mr-2 text-emerald-500" />
            Уровень физической активности
          </label>
          <select
            id="activityLevel"
            name="activityLevel"
            value={formData.activityLevel}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.activityLevel ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите уровень активности</option>
            {activityLevels.map((level) => (
              <option key={level.id} value={level.id}>
                {level.name}
              </option>
            ))}
          </select>
          {errors.activityLevel ? (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.activityLevel}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {activityLevels.find(level => level.id === formData.activityLevel)?.description || ''}
            </p>
          )}
        </div>

        {/* Тип телосложения */}
        <div>
          <label htmlFor="bodyType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип телосложения
          </label>
          <select
            id="bodyType"
            name="bodyType"
            value={formData.bodyType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.bodyType ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите тип телосложения</option>
            {bodyTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.bodyType ? (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.bodyType}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {bodyTypes.find(type => type.id === formData.bodyType)?.description || ''}
            </p>
          )}
        </div>
      </div>

      {/* Пояснение на основе выбранных параметров */}
      {formData.activityLevel && formData.bodyType && (
        <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            {getBodyTypeActivityMessage(formData.bodyType, formData.activityLevel, bodyTypes, activityLevels)}
          </p>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для генерации сообщения на основе типа телосложения и активности
const getBodyTypeActivityMessage = (bodyType, activityLevel, bodyTypes, activityLevels) => {
  const type = bodyTypes.find(t => t.id === bodyType);
  const activity = activityLevels.find(a => a.id === activityLevel);
  
  if (!type || !activity) return '';
  
  let message = '';
  
  // Сообщения для астенического типа
  if (bodyType === 'asthenic') {
    if (['sedentary', 'light'].includes(activityLevel)) {
      message = 'При астеническом телосложении рекомендуется увеличить физическую активность и включить силовые тренировки для набора мышечной массы.';
    } else if (['moderate', 'active'].includes(activityLevel)) {
      message = 'С вашим уровнем активности рекомендуется сбалансированное питание с повышенным содержанием белка для поддержки мышечной массы.';
    } else {
      message = 'Для астеников с высокой активностью важен режим восстановления и достаточное питание, чтобы избежать истощения и переутомления.';
    }
  } 
  // Сообщения для нормостенического типа
  else if (bodyType === 'normal') {
    if (['sedentary', 'light'].includes(activityLevel)) {
      message = 'При нормостеническом типе и низкой активности рекомендуется увеличить физическую нагрузку для поддержания здорового веса.';
    } else if (['moderate', 'active'].includes(activityLevel)) {
      message = 'Ваш уровень активности оптимален для поддержания здорового веса при нормостеническом телосложении.';
    } else {
      message = 'С высоким уровнем активности важно следить за полноценным питанием и восстановлением для поддержания результатов.';
    }
  } 
  // Сообщения для гиперстенического типа
  else if (bodyType === 'hypersthenic') {
    if (['sedentary', 'light'].includes(activityLevel)) {
      message = 'При гиперстеническом телосложении и низкой активности существует повышенный риск набора лишнего веса. Рекомендуется увеличить физическую активность.';
    } else if (['moderate', 'active'].includes(activityLevel)) {
      message = 'Ваш уровень активности подходит для контроля веса. Рекомендуется сочетать кардио и силовые тренировки.';
    } else {
      message = 'Высокий уровень активности поможет эффективно контролировать вес. Обратите внимание на правильное питание и режим тренировок.';
    }
  }
  
  return message;
};

export default AdditionalParametersSection;